import { useRef, useState } from "react";
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { bulkImportCases } from "@/lib/api";

export default function BulkImportDialog({ open, onOpenChange, onImported }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const doUpload = async (file) => {
    if (!file) return;
    setUploading(true); setResult(null);
    try {
      const r = await bulkImportCases(file);
      setResult(r);
      toast.success(`${r.created}/${r.total_rows} dava içeri aktarıldı`);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İçeri aktarım başarısız");
    } finally { setUploading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setResult(null); }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-xl" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-amber-500">UYAP Toplu Dava Aktarımı</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm text-slate-300">
            CSV veya Excel (.xlsx) dosyası yükleyin. Sütun başlıkları:
          </div>
          <div className="font-mono text-xs text-slate-400 bg-slate-950/60 border border-slate-800 rounded p-3">
            dava_no, mahkeme_adi, muvekkil, karsi_taraf, konu, is_bedeli, odeme_tipi, taksit_sayisi
          </div>
          <div className="text-xs text-slate-500">
            <strong>odeme_tipi</strong>: <code>pesin</code> veya <code>taksit</code>. Zorunlu sütunlar: <code>dava_no</code>, <code>mahkeme_adi</code>.
          </div>

          <input
            ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) doUpload(f); e.target.value = ""; }}
          />

          <div className="dropzone">
            <FileSpreadsheet className="w-10 h-10 text-amber-500 mx-auto mb-2" />
            <div className="text-sm text-slate-200 font-medium">Dosyayı seçin</div>
            <div className="text-xs text-slate-500 mt-1">.csv, .xlsx</div>
            <Button onClick={() => inputRef.current?.click()} disabled={uploading}
              className="bg-amber-600 hover:bg-amber-700 text-slate-950 mt-3" size="sm"
              data-testid="bulk-import-select-btn">
              <Upload className="w-4 h-4 mr-1" /> {uploading ? "Aktarılıyor..." : "Dosya Seç"}
            </Button>
          </div>

          {result && (
            <div className="border border-slate-800 rounded p-4 space-y-2 bg-slate-950/40">
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle2 className="w-4 h-4" />
                <span className="text-sm font-medium">{result.created} / {result.total_rows} dava başarıyla içeri aktarıldı</span>
              </div>
              {result.errors?.length > 0 && (
                <div className="pt-2 border-t border-slate-800">
                  <div className="flex items-center gap-2 text-red-400 mb-2">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">{result.errors.length} satırda hata</span>
                  </div>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {result.errors.map((e, i) => (
                      <div key={i} className="text-xs font-mono text-slate-400">Satır {e.row}: {e.error}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Kapat</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
